import { getLocalTimeZone, toZoned } from "@internationalized/date";
import { EditOutline18Icon } from "@vygruppen/spor-icon-react";
import { format } from "date-fns";
import { WithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { Badges } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import {
  getDisplayDuration,
  sortByDisplayDuration,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/utils";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { MessageInternal } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/Version";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import { HStack, IconButton, Stack } from "shared/components/Spor";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Text } from "shared/components/typography/TitleStyles";
import { isPlannedInfraStructureEvent } from "shared/utils/infrastructureEvent";
import { useDropsRole } from "stores/useDropsRole";
import { css, useTheme } from "styled-components";

type UpcomingEventProps = {
  data: WithInternalMessages<InfrastructureResponse>[];
  onEditClick: (event: WithInternalMessages<InfrastructureResponse>) => void;
};

export const UpcomingEventsList: FC<UpcomingEventProps> = ({
  data,
  onEditClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isVaktlederOrDriftledningen } = useDropsRole();
  if (data.length === 0)
    return (
      <NoDataMessage
        reason={t("Det er ikke registrert noen fremtidige hendelser")}
      />
    );
  return (
    <Stack gap="18px">
      {data.sort(sortByDisplayDuration).map((event) => (
        <Stack key={event.referenceId} gap={0}>
          <SegmentedPanel
            panelStyle={css`
              background: ${theme.colorBackgroundTertiary};
              border-radius: 12px;
              padding: 18px;
              gap: 6px;
            `}
            dashedLineVariant="tertiary"
          >
            <Stack>
              <HStack alignItems="center" justifyContent="space-between">
                <Text bold>
                  {[event.isirkId, event.stretchName]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
                {isVaktlederOrDriftledningen() && (
                  <IconButton
                    onClick={() => onEditClick(event)}
                    size="sm"
                    aria-label="Rediger"
                    variant="tertiary"
                    icon={<EditOutline18Icon />}
                  />
                )}
              </HStack>
              <Badges
                eventCategory={
                  isPlannedInfraStructureEvent(event.type)
                    ? "plannedInfrastructure"
                    : "futureInfrastructure"
                }
                displayDuration={getDisplayDuration(event.durations)}
              />
              <Text>{event.message}</Text>
              <Text secondary>
                {format(
                  toZoned(event.updatedAt, getLocalTimeZone()).toDate(),
                  "'Sist oppdatert' dd.MM.yyyy 'kl.' HH:mm",
                )}
              </Text>
            </Stack>
            {event.internalMessages && event.internalMessages.length > 0 && (
              <Stack pb="6px !important">
                <MessageInternal internalMessages={event.internalMessages} />
              </Stack>
            )}
          </SegmentedPanel>
        </Stack>
      ))}
    </Stack>
  );
};
